import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DEFAULT_CONTENT_LOCALE,
  getFirstLessonLocation,
  getNextIncompleteLessonLocation,
  useTheme,
  type Course,
  type ExportedCourse,
} from '@course-authoring/shared-types';
import {
  AskEmaWidget,
  AudioControlsProvider,
  CollapsibleNavLayout,
  ContentFooter,
  CourseCompleteBanner,
  CourseCompletionView,
  CourseNavigation,
  shouldShowLessonLanguageFallback,
  LanguageUnavailableBanner,
  PrivacyNotice,
  SaveExitBar,
  useLearnerCourseFlow,
  UserToolbar,
  WelcomeModal,
} from '@course-authoring/ui-blocks';
import { ScopedI18nProvider, useTranslation, type TFunction } from '@course-authoring/i18n';
import { LessonPreviewCanvas } from '../components/PreviewCanvas';
import { fetchDraftCoursePreview, type DraftCoursePreviewData } from '../api/exportSourceLoader';
import { courseLearnerPreviewKeys } from '../api/lessonLocaleCoverageQueries';
import { fetchCertificatePreview } from '../api/certificateApi';
import { ResolvedThemeProvider } from '../theme/ResolvedThemeProvider';
import { ResolvedAccountConfigProvider } from '../config/ResolvedAccountConfigProvider';
import { WorkflowStatusBadge, deriveBadgeState } from '../components';
import { useAskExpertPreviewOverride } from '../hooks/useClientContext';
import { getRememberedListPath } from '../hooks/useListPathMemory';
import { createCourseEditorPageStyles } from './courseEditor/courseEditorPageStyles';

const COURSE_CANVAS_SCROLL_ROOT_SELECTOR = '[data-testid="course-canvas-scroll-root"]';

interface CourseLearnerPreviewProps {
  courseId: string;
  variant?: 'page' | 'embedded';
  selectedLocale?: string;
  onLocaleChange?: (locale: string) => void;
  /** Active client account for account-resolved previews; null = base. */
  accountId?: number | null;
  /** Optional bar rendered above the preview; receives the loaded course title and record. */
  renderTopBar?: (courseTitle: string, course: Course) => ReactNode;
}

export { courseLearnerPreviewKeys } from '../api/lessonLocaleCoverageQueries';

function getEstimatedTimeLabel(
  course: Course,
  exportedCourse: ExportedCourse,
  t: TFunction,
): string | undefined {
  const minutes = typeof course.durationMinutes === 'number'
    ? course.durationMinutes
    : exportedCourse.lessons.reduce((total, lesson) => total + (lesson.estimatedTimeMinutes ?? 0), 0);

  return minutes > 0 ? t('player.estimated_time', { minutes }) : undefined;
}

export function CourseLearnerPreview({
  courseId,
  variant = 'page',
  selectedLocale: controlledLocale,
  onLocaleChange,
  accountId = null,
  renderTopBar,
}: CourseLearnerPreviewProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens, variant), [tokens, variant]);
  const [internalSelectedLocale, setInternalSelectedLocale] = useState(DEFAULT_CONTENT_LOCALE);
  const selectedLocale = controlledLocale ?? internalSelectedLocale;
  const previewScopeKey = JSON.stringify([courseId, accountId]);
  const handleLocaleChange = useCallback((locale: string) => {
    if (onLocaleChange) {
      onLocaleChange(locale);
      return;
    }
    setInternalSelectedLocale(locale);
  }, [onLocaleChange]);

  const { data, isLoading, error } = useQuery({
    queryKey: courseLearnerPreviewKeys.detail(courseId, selectedLocale, accountId),
    queryFn: () => fetchDraftCoursePreview(courseId, selectedLocale, accountId),
    enabled: courseId.length > 0,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: undefined,
  });

  const [lastLoadedPreview, setLastLoadedPreview] = useState<{
    scopeKey: string;
    data: DraftCoursePreviewData;
  } | null>(null);
  useEffect(() => {
    if (data) setLastLoadedPreview({ scopeKey: previewScopeKey, data });
  }, [data, previewScopeKey]);

  const previewData = data ?? (
    lastLoadedPreview?.scopeKey === previewScopeKey ? lastLoadedPreview.data : undefined
  );

  if (!previewData && isLoading) {
    return <div style={styles.feedback}>Loading course preview...</div>;
  }

  if (!previewData) {
    return (
      <div style={styles.feedback}>
        Unable to load course preview{error instanceof Error ? `: ${error.message}` : '.'}
      </div>
    );
  }

  // The shared learner-flow hook requires a loaded course, so the flow-backed
  // owner stays mounted while another locale is fetched. This keeps progress
  // and pacing alive while the visible content shows its loading/error state.
  return (
    <ScopedI18nProvider locale={selectedLocale}>
      <CourseLearnerPreviewContent
        key={previewScopeKey}
        data={previewData}
        variant={variant}
        selectedLocale={selectedLocale}
        onLocaleChange={handleLocaleChange}
        courseId={courseId}
        accountId={accountId}
        renderTopBar={renderTopBar}
        isLoading={!data && isLoading}
        error={error}
      />
    </ScopedI18nProvider>
  );
}

interface CourseLearnerPreviewContentProps {
  data: DraftCoursePreviewData;
  variant: CourseLearnerPreviewProps['variant'];
  selectedLocale: string;
  onLocaleChange: (locale: string) => void;
  courseId: string;
  accountId: number | null;
  renderTopBar: CourseLearnerPreviewProps['renderTopBar'];
  isLoading: boolean;
  error: unknown;
}

function CourseLearnerPreviewContent({
  data,
  variant,
  selectedLocale,
  onLocaleChange,
  courseId,
  accountId,
  renderTopBar,
  isLoading,
  error,
}: CourseLearnerPreviewContentProps) {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(tokens, variant), [tokens, variant]);
  const { course, exportedCourse } = data;

  // Shared learner-flow state (location, completion, course-complete signals,
  // navigation handlers). Preview layers per-lesson scroll restoration on top.
  const flow = useLearnerCourseFlow(exportedCourse);
  const {
    currentLocation,
    completionStatus,
    allLessonsComplete,
    isCourseComplete,
    hasCompletedCourse,
    currentLesson,
    currentBlocks,
    lessonProgressMap,
    navigationLessons,
    markBlockComplete,
    markCurrentLessonEngaged,
    handleLessonSelect: flowHandleLessonSelect,
    handleLessonCompleteContinue: flowHandleLessonCompleteContinue,
    shouldShowCompletedCourseLessonState,
  } = flow;

  const lessonHeadingRef = useRef<HTMLHeadingElement>(null);
  const visitedLessonIdsRef = useRef<Set<string>>(new Set());
  const lessonScrollTopByIdRef = useRef<Record<string, number>>({});
  const pendingScrollTopRef = useRef<number | null>(0);

  const [searchParams] = useSearchParams();
  const shouldShowWelcome = searchParams.get('welcomeModal') === 'true';
  // Privacy notice is a legal acknowledgment gate, not a themeable account
  // setting — preview has no logged-in learner to hold an acknowledgment
  // record, so it stays URL-param gated only (showPrivacy=1).
  const shouldShowPrivacy = searchParams.get('showPrivacy') === '1';
  const askExpertPreviewOverride = useAskExpertPreviewOverride();
  // Preview-only sandbox flag — lets reviewers see the SaveExitBar in the
  // course-learner preview (the bar is otherwise only mounted in the export
  // player). Not for production use.
  const shouldShowSaveExitBar = searchParams.get('saveExitBar') === 'true';
  const hasTopBar = shouldShowSaveExitBar || renderTopBar !== undefined;
  // Show WelcomeModal based on URL parameter
  const [welcomeDismissed, setWelcomeDismissed] = useState(!shouldShowWelcome);
  const [privacyDismissed, setPrivacyDismissed] = useState(false);
  // NA-408 "Show Block IDs": ephemeral, resets every mount (this component
  // remounts per `key={selectedLocale}`, and fresh per course launch) —
  // never persisted.
  const [showBlockIds, setShowBlockIds] = useState(false);

  // Preview fetches the PDF through author-api (which HMAC-signs the real hoot-server call) and opens it in a new tab
  const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);
  const handleDownloadCertificate = useCallback(() => {
    if (isDownloadingCertificate) {
      console.log('[Certificate Preview] ignoring click — a request is already in flight');
      return;
    }
    setIsDownloadingCertificate(true);
    void (async () => {
      try {
        console.log('[Certificate Preview] fetching preview PDF...', { selectedLocale });
        const { blob } = await fetchCertificatePreview(courseId, accountId, selectedLocale);
        console.log('[Certificate Preview] got PDF, opening in a new tab', { bytes: blob.size });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        // The new tab has already loaded the blob by the time it renders;
        // revoke shortly after rather than holding the object URL forever.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        console.error('[Certificate Preview] failed', err);
      } finally {
        setIsDownloadingCertificate(false);
      }
    })();
  }, [courseId, accountId, selectedLocale, isDownloadingCertificate]);

  // Reset the preview-only scroll bookkeeping whenever the course reloads.
  // Flow state itself is reset inside useLearnerCourseFlow.
  useEffect(() => {
    const firstLocation = getFirstLessonLocation(exportedCourse.lessons);
    visitedLessonIdsRef.current = new Set(firstLocation ? [firstLocation.lessonId] : []);
    lessonScrollTopByIdRef.current = {};
    pendingScrollTopRef.current = 0;
  }, [exportedCourse]);

  const estimatedTimeLabel = getEstimatedTimeLabel(course, exportedCourse, t);
  const shouldShowCourseCompleteSidebar =
    isCourseComplete || hasCompletedCourse || allLessonsComplete;

  const persistCurrentLessonScroll = useCallback(() => {
    if (!currentLocation) return;

    const scrollRoot = document.querySelector(COURSE_CANVAS_SCROLL_ROOT_SELECTOR);
    if (!(scrollRoot instanceof HTMLElement)) return;

    lessonScrollTopByIdRef.current[currentLocation.lessonId] = scrollRoot.scrollTop;
  }, [currentLocation]);

  const queueLessonScroll = useCallback((lessonId: string, options: { forceTop?: boolean } = {}) => {
    if (options.forceTop) {
      pendingScrollTopRef.current = 0;
      visitedLessonIdsRef.current.add(lessonId);
      return;
    }

    const hasVisited = visitedLessonIdsRef.current.has(lessonId);
    pendingScrollTopRef.current = hasVisited
      ? lessonScrollTopByIdRef.current[lessonId] ?? 0
      : 0;
    visitedLessonIdsRef.current.add(lessonId);
  }, []);

  const handleLessonSelect = useCallback((lessonId: string) => {
    persistCurrentLessonScroll();
    queueLessonScroll(lessonId, { forceTop: true });

    if (currentLocation?.lessonId === lessonId) {
      const scrollRoot = document.querySelector(COURSE_CANVAS_SCROLL_ROOT_SELECTOR);
      if (scrollRoot instanceof HTMLElement) {
        scrollRoot.scrollTop = 0;
      }
    }

    flowHandleLessonSelect(lessonId);
  }, [currentLocation?.lessonId, flowHandleLessonSelect, persistCurrentLessonScroll, queueLessonScroll]);

  const handleLessonCompleteContinue = useCallback(() => {
    // Re-derive the destination so scroll bookkeeping can be queued before the
    // flow performs the location transition.
    const nextIncomplete = getNextIncompleteLessonLocation(
      exportedCourse.lessons,
      currentLocation,
      lessonProgressMap
    );

    persistCurrentLessonScroll();
    if (nextIncomplete) {
      queueLessonScroll(nextIncomplete.lessonId, { forceTop: hasCompletedCourse });
    }

    flowHandleLessonCompleteContinue();
  }, [
    currentLocation,
    exportedCourse.lessons,
    flowHandleLessonCompleteContinue,
    hasCompletedCourse,
    lessonProgressMap,
    persistCurrentLessonScroll,
    queueLessonScroll,
  ]);

  useLayoutEffect(() => {
    if (!currentLocation || isCourseComplete) return;

    const scrollTop = pendingScrollTopRef.current;
    if (scrollTop === null) return;

    pendingScrollTopRef.current = null;
    const scrollRoot = document.querySelector(COURSE_CANVAS_SCROLL_ROOT_SELECTOR);
    if (scrollRoot instanceof HTMLElement) {
      scrollRoot.scrollTop = scrollTop;
    }
  }, [currentLocation?.lessonId, isCourseComplete]);

  useEffect(() => {
    if (currentLesson && lessonHeadingRef.current) {
      lessonHeadingRef.current.focus();
    }
  }, [currentLesson?.id]);

  if (isLoading) {
    return <div style={styles.feedback}>Loading course preview...</div>;
  }

  if (error) {
    return (
      <div style={styles.feedback}>
        Unable to load course preview{error instanceof Error ? `: ${error.message}` : '.'}
      </div>
    );
  }

  if (exportedCourse.lessons.length === 0) {
    return (
      <div style={styles.feedback}>
        Add at least one lesson to preview this course.
      </div>
    );
  }

  const lessonIndex = currentLesson
    ? exportedCourse.lessons.findIndex((lesson) => lesson.id === currentLesson.id) + 1
    : undefined;
  const showLanguageFallback = currentLesson
    ? shouldShowLessonLanguageFallback({
        selectedLocale,
        defaultLocale: currentLesson.defaultLocale ?? exportedCourse.defaultLocale,
        fullyTranslatedLocales: currentLesson.fullyTranslatedLocales,
        availableLocales: currentLesson.availableLocales,
      })
    : false;
  const lessonNotices = [
    ...(showLanguageFallback
      ? [
          <LanguageUnavailableBanner
            key="language-unavailable"
          />,
        ]
      : []),
    ...(shouldShowCompletedCourseLessonState
      ? [
          <CourseCompleteBanner
            key="course-complete"
            certificateEnabled={course.completionCertificateEnabled}
            onDownloadCertificate={handleDownloadCertificate}
            isDownloadingCertificate={isDownloadingCertificate}
          />,
        ]
      : []),
  ];
  const lessonTopContent = lessonNotices.length > 0
    ? (
        <div style={styles.noticeStack} data-testid="lesson-notice-stack">
          {lessonNotices}
        </div>
      )
    : undefined;

  const previewBody = (
    <div
      lang={selectedLocale}
      style={{
        ...styles.container,
        ...(hasTopBar ? { height: 'auto', flex: 1, minHeight: 0 } : {}),
      }}
      data-testid="course-learner-preview"
    >
      {shouldShowPrivacy && !privacyDismissed && (
        <PrivacyNotice onDismiss={() => setPrivacyDismissed(true)} />
      )}
      {!(shouldShowPrivacy && !privacyDismissed) && shouldShowWelcome && !welcomeDismissed && (
        <WelcomeModal
          courseTitle={exportedCourse.title}
          isTimed={Boolean(exportedCourse.timedCourse)}
          minimumRequiredMinutes={exportedCourse.minimumRequiredMinutes}
          enableAskExpert={askExpertPreviewOverride}
          onDismiss={() => setWelcomeDismissed(true)}
        />
      )}
      <CollapsibleNavLayout
        renderNav={(closeDrawer) => (
          <CourseNavigation
            title={exportedCourse.title}
            lessons={navigationLessons}
            currentLessonId={isCourseComplete ? null : currentLocation?.lessonId ?? null}
            lessonProgress={lessonProgressMap}
            onLessonSelect={(lessonId) => {
              handleLessonSelect(lessonId);
              closeDrawer();
            }}
            layout="assignmentPanel"
            estimatedTimeLabel={estimatedTimeLabel}
            allowLessonSkipping
            isCourseComplete={shouldShowCourseCompleteSidebar}
            data-testid="course-preview-assignment-panel"
          />
        )}
        renderToolbar={(variant) => (
          <UserToolbar
            variant={variant}
            selectedLocale={selectedLocale}
            onLanguageSelect={onLocaleChange}
            isPreview
            showBlockIds={showBlockIds}
            onShowBlockIdsChange={setShowBlockIds}
          />
        )}
      >
      <main style={styles.main}>
        {currentLesson ? (
          <h2
            ref={lessonHeadingRef}
            style={styles.visuallyHiddenHeading}
            tabIndex={-1}
            data-testid="course-preview-lesson-heading"
          >
            {currentLesson.title}
          </h2>
        ) : null}

        <div style={styles.content}>
          {isCourseComplete ? (
            <div style={styles.completionScroll}>
              <CourseCompletionView
                courseTitle={course.title}
                completedMessage={course.completedMessage}
                certificateEnabled={course.completionCertificateEnabled}
                onDownloadCertificate={handleDownloadCertificate}
                isDownloadingCertificate={isDownloadingCertificate}
              />
              <ContentFooter />
            </div>
          ) : (
            <LessonPreviewCanvas
              pacing={flow.pacing}
              blocks={currentBlocks}
              completionStatus={completionStatus}
              onBlockComplete={markBlockComplete}
              enableCompletionTracking
              enableSuccessAnimations
              enableBlockReveal={false}
              topContent={lessonTopContent}
              lessonTitle={currentLesson?.title}
              lessonThemeColor={currentLesson?.themeColor}
              currentLocale={selectedLocale}
              courseNumericId={exportedCourse.numericId}
              lessonNumericId={currentLesson?.numericId}
              lessonLocale={currentLesson?.locale}
              showBlockIds={showBlockIds}
              lessonIndex={lessonIndex}
              totalLessons={exportedCourse.lessons.length}
              onLessonCompleteContinue={handleLessonCompleteContinue}
              footerContent={<ContentFooter />}
              onEngage={markCurrentLessonEngaged}
            />
          )}
        </div>
      </main>
      <AskEmaWidget isPreview askExpertEnabled={askExpertPreviewOverride} />
      </CollapsibleNavLayout>
    </div>
  );

  return (
    <AudioControlsProvider
      lessonId={currentLocation?.lessonId ?? null}
      blocks={currentBlocks}
      narration={exportedCourse.narration}
    >
      {hasTopBar ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            // Fill the pane's width; without this the column wrapper shrinks to
            // content and leaves blank space on the right when the surrounding
            // nav collapses and the pane widens.
            width: '100%',
            minWidth: 0,
          }}
        >
          {shouldShowSaveExitBar && <SaveExitBar onSaveExit={() => undefined} />}
          {renderTopBar?.(exportedCourse.title, course)}
          {previewBody}
        </div>
      ) : (
        previewBody
      )}
    </AudioControlsProvider>
  );
}

function CoursePreviewTopBar({ title, course }: { title: string; course: Course }) {
  const { tokens } = useTheme();
  const navigate = useNavigate();
  const styles = useMemo(() => createCourseEditorPageStyles(tokens), [tokens]);

  return (
    <header style={styles.topBar}>
      <button
        type="button"
        style={styles.backButton}
        onClick={() => navigate(getRememberedListPath('/courses'))}
      >
        ← Courses
      </button>
      <h2 style={{ ...styles.titleInput, margin: 0 }}>{title}</h2>
      {/* This bar only stands in for the editor's controls on view-only Draft
          content (NA-210); the badge says why those controls are absent. */}
      {(course.workflowState ?? 'draft') === 'draft' && (
        <WorkflowStatusBadge state={deriveBadgeState('draft', course.publishLifecycle)} />
      )}
    </header>
  );
}

export function CourseLearnerPreviewPage() {
  const { courseId } = useParams<{ courseId: string }>();

  if (!courseId) {
    return <div>Unable to load course preview.</div>;
  }

  return (
    <ResolvedThemeProvider>
      <ResolvedAccountConfigProvider>
        <CourseLearnerPreview
          courseId={courseId}
          variant="embedded"
          renderTopBar={(title, course) => <CoursePreviewTopBar title={title} course={course} />}
        />
      </ResolvedAccountConfigProvider>
    </ResolvedThemeProvider>
  );
}

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
  variant: CourseLearnerPreviewProps['variant']
): Record<string, CSSProperties> {
  return {
    container: {
      position: 'relative',
      height: variant === 'embedded' ? '100%' : '100vh',
      width: '100%',
      minWidth: 0,
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.sidebar,
      fontFamily: tokens.typography.fontFamily,
    },
    main: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.page,
    },
    content: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    completionScroll: {
      flex: 1,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
    noticeStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    canvas: {
      flex: 1,
      minHeight: 0,
    },
    feedback: {
      minHeight: variant === 'embedded' ? '100%' : '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: tokens.spacing["2xl"],
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.page,
      fontFamily: tokens.typography.fontFamily,
    },
    visuallyHiddenHeading: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0,
      outline: 'none',
    },
  };
}

CourseLearnerPreviewPage.displayName = 'CourseLearnerPreviewPage';
